import React, { useState, useEffect } from "react";
import { api } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { toast } from "sonner";

export function DigestPanel() {
  const [stats, setStats] = useState(null);
  const [override, setOverride] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/digest/preview").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const send = async () => {
    setBusy(true);
    try {
      const payload = override.trim() ? { override_email: override.trim().toLowerCase() } : {};
      const r = await api.post("/admin/digest/send-now", payload);
      if (r.data.ok) {
        const sent = r.data.sent || [];
        if (sent.length) toast.success(`Digest sent to ${sent.join(", ")}`);
        if ((r.data.failed || []).length) toast.warning(`Failed: ${r.data.failed.map(f => f.email).join(", ")}`);
      } else {
        toast.error(r.data.reason || "Digest did not send");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send digest");
    } finally { setBusy(false); }
  };

  if (!stats) return null;
  const hasInsights = (stats.top_receivers && stats.top_receivers.length) || stats.abandoned_pending || stats.abandoned_zero;
  return (
    <div className="bg-[#112318] text-[#FBF9F6] rounded-2xl p-6 space-y-6" data-testid="digest-panel">
      <div className="flex flex-col lg:flex-row gap-6 items-stretch">
        <div className="flex-1">
          <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">Monthly digest · {stats.month_label}</p>
          <p className="serif text-2xl mt-1">
            ${stats.live_balance.toLocaleString(undefined, { minimumFractionDigits: 2 })} held · {stats.active_cards} active cards
          </p>
          <p className="text-sm opacity-70 mt-1">
            Last month: ${stats.loaded_total.toLocaleString(undefined, { minimumFractionDigits: 2 })} loaded ·
            {" "}{stats.issued_count} issued ·
            {" "}{stats.tickets_new} tickets opened ·
            {" "}{stats.refunds_count} refunds
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase opacity-60">Override recipient (optional)</label>
            <input
              type="email"
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              placeholder="you@verified-domain.com"
              className="block mt-1 bg-transparent border-b border-white/20 focus:border-[#8BA888] focus:outline-none py-1.5 mono text-sm text-[#FBF9F6] placeholder:text-white/40 w-72"
              data-testid="digest-override-input"
            />
          </div>
          <Button
            onClick={send}
            disabled={busy}
            className="rounded-xl bg-[#8BA888] hover:bg-[#6F8E70] text-[#0A1F16] font-medium"
            data-testid="digest-send-btn"
          >
            {busy ? "Sending…" : "Send digest now"}
          </Button>
        </div>
      </div>

      {hasInsights && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4 border-t border-white/10" data-testid="digest-insights">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase opacity-60 mb-3">Top receivers · lifetime</p>
            {(stats.top_receivers || []).length === 0 ? (
              <p className="text-sm opacity-60">No receivers yet.</p>
            ) : (
              <ul className="space-y-2">
                {stats.top_receivers.map((r) => (
                  <li key={r.email} className="flex items-baseline justify-between text-sm" data-testid={`digest-receiver-${r.email}`}>
                    <div className="min-w-0">
                      <p className="truncate">{r.name || r.email}</p>
                      <p className="text-[11px] opacity-50 mono truncate">{r.email}</p>
                    </div>
                    <span className="mono text-[#8BA888]">{r.cards} card{r.cards !== 1 ? "s" : ""}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase opacity-60 mb-3">Needs attention</p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-baseline justify-between">
                <span className="opacity-80">Pending payment · 14+ days</span>
                <span className={`mono ${stats.abandoned_pending > 0 ? "text-[#D96C4A]" : "text-[#8BA888]"}`} data-testid="digest-abandoned-pending">{stats.abandoned_pending}</span>
              </li>
              <li className="flex items-baseline justify-between">
                <span className="opacity-80">Active · $0 balance · 7+ days</span>
                <span className={`mono ${stats.abandoned_zero > 0 ? "text-[#D96C4A]" : "text-[#8BA888]"}`} data-testid="digest-abandoned-zero">{stats.abandoned_zero}</span>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
